import React, { useState } from 'react';
import Modal from '../../common/Modal';
import FormField from '../../common/FormField';
import { useFormData } from '../../../hooks/useFormData';

/**
 * Customer add/edit modal.
 *
 * Before:  232 lines — had its own modalOverlayStyle, modalContentStyle,
 *          inputStyle, buttonGroupStyle (duplicated in 5 other modals),
 *          plus its own useState/useEffect/handleChange boilerplate.
 * After:   ~80 lines — uses shared Modal shell, FormField inputs,
 *          and useFormData hook.  Only customer-specific validation
 *          (name required + duplicate name check) remains here.
 */

const DEFAULTS = {
  full_name: '',
  street: '',
  city: '',
  state: '',
  postcode: '',
  email: '',
  phone_number: '',
  abn: '',
};

export default function CustomerModal({
  isOpen, onClose, onSubmit, initialData, title,
  existingCustomers = [],
}) {
  const { formData, handleChange } = useFormData(DEFAULTS, initialData, isOpen);
  const [nameError, setNameError] = useState('');

  const handleSubmit = () => {
    const trimmedName = (formData.full_name || '').trim();

    if (!trimmedName) {
      setNameError('Contact name is required.');
      return;
    }

    // Duplicate-name check (case-insensitive), excluding current record
    const duplicate = existingCustomers.find(
      (c) =>
        c.full_name?.trim().toLowerCase() === trimmedName.toLowerCase() &&
        (!initialData || c.id !== initialData.id)
    );
    if (duplicate) {
      setNameError('Contact name must be unique.');
      return;
    }

    setNameError('');
    onSubmit(formData);
  };

  const handleFieldChange = (e) => {
    handleChange(e);
    if (e.target.name === 'full_name') setNameError('');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} onSubmit={handleSubmit} title={title}>
      <div className="flex flex-col gap-1">
        <FormField name="full_name" label="Contact name" value={formData.full_name} onChange={handleFieldChange} required error={nameError} />
        <FormField name="street" label="Street" value={formData.street} onChange={handleChange} />
        <FormField name="city" label="City" value={formData.city} onChange={handleChange} />
        <FormField name="state" label="State" value={formData.state} onChange={handleChange} />
        <FormField name="postcode" label="Postcode" value={formData.postcode} onChange={handleChange} />
        <FormField name="email" label="Email" value={formData.email} onChange={handleChange} />
        <FormField name="phone_number" label="Phone Number" value={formData.phone_number} onChange={handleChange} />
        <FormField name="abn" label="ABN" value={formData.abn} onChange={handleChange} />
      </div>
    </Modal>
  );
}
